import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Project } from 'src/app/shared/models';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { Ability } from '@casl/ability';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { AppService, AuthService } from '../../../../../../shared/services';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../shared/components';
import { ProjectsService } from '../../../../core/projects.service';
import { EditProjectDetailsComponent } from '../edit-project-details/edit-project-details.component';

@Component({
    selector: 'app-projects-page-each-project-card',
    templateUrl: './project-card.component.html',
    styleUrls: ['./project-card.component.scss'],
    standalone: false
})
export class ProjectCardComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() showControls = true;
  @Input() showProgress = false;
  @Input() picHeight = '150px';
  @Input() project: any;
  @ViewChild('projectTitle') projectTitleRef: ElementRef<HTMLElement>;
  initialized = false;
  role: any;
  actingRole: any;
  account: any;
  canEdit = true;
  tooltipDisabled = true;
  protected readonly moment = moment;

  constructor(
    private router: Router,
    @Inject(Ability) private ability: Ability,
    private appSrv: AppService,
    private authService: AuthService,
    private dialog: MatDialog,
    private projectsService: ProjectsService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  private navigateToProjectDetails(project: any): void {
    if (this.actingRole !== 'reviewer' && this.actingRole !== 'contractor-basic') {
      this.router.navigate([`/projects/${project.id}`], {
        queryParams: {
          tab: 'sites',
          status: ['submitted-reviewed', 'approved', 'draft'],
          startDate: moment().subtract(15, 'days').format('YYYY-MM-DD'),
          endDate: moment().format('YYYY-MM-DD'),
        },
      });
    } else {
      this.router.navigate([`/projects/${project.id}`], {
        queryParams: {
          tab: 'submittals',
        },
      });
    }
  }

  redirectToProjectDetails(project: any): void {
    const isFormDirty = this.projectsService.isAnyFormDirty.getValue();
    if (!isFormDirty) {
      this.projectsService.isAnyFormDirty.next(false);
      this.projectsService.closeRightPanel.next();
      this.navigateToProjectDetails(project);
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Unsaved changes',
        'There are unsaved changes.<br/>Are you sure you want to discard?',
      ),
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.projectsService.isAnyFormDirty.next(false);
        this.projectsService.closeRightPanel.next();
        this.navigateToProjectDetails(project);
      }
    });
  }

  ngOnInit(): void {
    if (this.project) {
      this.initialized = true;

      this.account = this.authService.getCurrentUser();
      this.role = this.account.roles.find(obj => obj.project_id === this.project?.id)?.role;

      if (!!this.role?.includes('reviewer')) {
        this.actingRole = 'reviewer';
      }

      if (!!this.role?.includes('contractor-basic')) {
        this.actingRole = 'contractor-basic';
      }

      if (!!this.role?.includes('project-manager')) {
        this.actingRole = 'project-manager';
      }

      this.canEdit = this.ability.can('update', this.project);
    }
  }

  ngAfterViewInit(): void {
    this.updateTooltipState();
  }

  private updateTooltipState(): void {
    const el = this.projectTitleRef?.nativeElement;
    this.tooltipDisabled = !(el && el.scrollWidth > el.clientWidth);
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['project'] && !changes['project'].firstChange) {
      setTimeout(() => this.updateTooltipState());
    }
  }

  editProject(project: Project): void {
    this.router.navigate([], {
      queryParams: { selectedId: project.id },
      queryParamsHandling: 'merge',
    });

    this.projectsService.openRightPanel.next({
      component: EditProjectDetailsComponent,
      data: project,
    });
  }
}
